import { useMutation, useQuery } from 'react-query';
import {
  connectBankAccount,
  getBankAccounts,
  createPlaidLinkToken,
  removeBankAccount,
  refreshBankAccounts,
  createHurdlrLinkTokenForUpdate,
  ConnectBankAccountParams,
} from 'services/bankAccounts';
import { useAuth0 } from '@auth0/auth0-react';

export const useBankAccounts = (businessId: string | undefined) => {
  const { isAuthenticated } = useAuth0();
  const { data, ...rest } = useQuery(
    ['bank-accounts', businessId],
    () => getBankAccounts(businessId ?? ''),
    {
      enabled: !!businessId && isAuthenticated,
    },
  );
  return {
    bankAccounts: data ?? [],
    ...rest,
  };
};

export const usePlaidLinkToken = (businessId: string) => {
  const { data, ...rest } = useQuery(
    ['hurdlr-plaid-link-token', businessId],
    () => createPlaidLinkToken(businessId),
    {
      refetchOnMount: 'always', // plaid link token should be refreshed when dialog is opened
    },
  );
  return {
    linkToken: data?.linkToken ?? '',
    ...rest,
  };
};

export const useHurdlrLinkTokenForUpdate = (
  businessId: string,
  plaidItemId: string,
  enableAccountSelection: boolean = false,
) => {
  const { data, ...rest } = useQuery(
    ['plaid-update-link-token', businessId, plaidItemId],
    () =>
      createHurdlrLinkTokenForUpdate(
        businessId,
        plaidItemId,
        enableAccountSelection,
      ),
  );
  return {
    linkToken: data?.linkToken ?? '',
    ...rest,
  };
};

export const useConnectBankAccount = () => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    (params: ConnectBankAccountParams) => connectBankAccount(params),
  );

  return {
    connectBankAccount: mutate,
    connectBankAccountAsync: mutateAsync,
    ...rest,
  };
};

export const useRemoveBankAccount = () => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ({
      businessId,
      plaidItemId,
    }: {
      businessId: string;
      plaidItemId: string;
    }) => removeBankAccount(businessId, plaidItemId),
  );

  return {
    removeBankAccount: mutate,
    removeBankAccountAsync: mutateAsync,
    ...rest,
  };
};

export const useRefreshBankAccounts = () => {
  const { mutate, mutateAsync, ...rest } = useMutation((businessId: string) =>
    refreshBankAccounts(businessId),
  );

  return {
    refreshBankAccounts: mutate,
    refreshBankAccountsAsync: mutateAsync,
    ...rest,
  };
};
